/**
 * MuteDeviceDialog — pick how long to suppress a device's alarms.
 *
 * Opened from the Suppress Alarms button in the Status & Sensors card header.
 * Resuming alarms does not come through here: that is one click on the banner the
 * page shows while a device is suppressed.
 *
 * No typed confirmation. The consequence is stated in the body and the confirm
 * button names the window it is about to set, which is the deliberation this
 * action warrants — a mute is reversible and expires on its own, and staff do it
 * routinely for maintenance, so a typed gate would just get muscle-memoried.
 *
 * Mount it conditionally (`{open ? <MuteDeviceDialog … /> : null}`), which is what
 * resets the chosen duration between uses.
 */

import { useState } from 'react';
import { MUTE_DURATIONS, DEFAULT_MUTE_MINUTES, muteUntilTimestamp } from '../../../../utils/deviceMute.js';
import { convertIsoUTCToLocal } from '../../../../libs.jsx';

const MuteDeviceDialog = ({ deviceName, tzOffset, onClose, onConfirm, saving }) => {
  const [minutes, setMinutes] = useState(DEFAULT_MUTE_MINUTES);

  const label = MUTE_DURATIONS.find((d) => d.minutes === Number(minutes))?.label || '';
  /* Previewed with the same helper and offset the banner will use, so the time
     promised here is the time shown afterwards. Recomputed on render rather than
     pinned when the dialog opened — a minute of hesitation would otherwise make
     the preview a minute optimistic. */
  const endsAt = convertIsoUTCToLocal(muteUntilTimestamp(Number(minutes)), tzOffset);

  return (
    <div className="pp-dialog-backdrop" onClick={onClose}>
      <div className="pp-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="pp-dialog__header">
          <h2 className="pp-dialog__title">Suppress Alarms</h2>
          <button className="dp-drawer__close" onClick={onClose}>&#x2715;</button>
        </div>

        <div className="pp-dialog__body">
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            Stop <strong>{deviceName || 'this device'}</strong> from producing alarms for a set
            period.
          </p>

          {/* The operational warning — normal body text, not muted. This is the
              line that says nobody will be alerted. */}
          <p style={{ margin: 'var(--space-3) 0 0', lineHeight: 1.6 }}>
            While suppressed it will not produce any alarms. Nobody will be alerted if it is
            triggered.
          </p>

          <div className="dp-drawer__field" style={{ marginTop: 'var(--space-4)' }}>
            <label className="dp-drawer__label" htmlFor="dpp-mute-duration">
              Suppress for
            </label>
            <select
              id="dpp-mute-duration"
              className="dp-select"
              value={minutes}
              onChange={(e) => setMinutes(Number(e.target.value))}
              disabled={saving}
              autoFocus
            >
              {MUTE_DURATIONS.map((d) => (
                <option key={d.minutes} value={d.minutes}>{d.label}</option>
              ))}
            </select>
            <p
              style={{
                margin: 'var(--space-2) 0 0',
                color: 'var(--color-text-muted)',
                fontSize: 'var(--font-size-xs)',
              }}
            >
              Alarms resume automatically at {endsAt}. You can also resume them early from the
              banner on this page.
            </p>
          </div>
        </div>

        <div className="pp-dialog__footer">
          <button className="dp-btn dp-btn--cancel" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          {/* Names the window, so the last thing read before clicking is the
              specific thing about to happen. */}
          <button
            className="dp-btn dp-btn--primary"
            onClick={() => onConfirm(Number(minutes))}
            disabled={saving}
          >
            {saving ? 'Suppressing…' : `Suppress for ${label}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MuteDeviceDialog;
